import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Cloud, Shield, Database, Activity, Zap, CheckCircle2, Server } from 'lucide-react';
import { heroStats, images } from '../mock/mock';

const Hero = () => {
  return (
    <section id="home" className="relative overflow-hidden section-sky">
      {/* Soft cloud decorations */}
      <div className="absolute top-10 left-10 w-40 h-24 bg-white/60 rounded-full blur-2xl cloud-float" />
      <div className="absolute top-32 right-16 w-56 h-32 bg-white/70 rounded-full blur-3xl cloud-float" style={{ animationDelay: '2s' }} />
      <div className="absolute bottom-32 left-1/4 w-48 h-28 bg-white/60 rounded-full blur-3xl cloud-float" style={{ animationDelay: '4s' }} />

      {/* Soft gradient orbs */}
      <div className="absolute -top-24 -left-24 w-[500px] h-[500px] rounded-full bg-cyan-200/40 blur-[120px]" />
      <div className="absolute -bottom-32 -right-24 w-[500px] h-[500px] rounded-full bg-blue-200/40 blur-[120px]" />

      <div className="container-x px-6 pt-14 pb-24 md:pt-20 md:pb-32 relative">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          {/* Left content */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 backdrop-blur border border-sky-200 text-sky-700 text-xs font-semibold mb-6 shadow-sm"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              LIVE · All Systems Operational · Trusted Since 2006
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-black leading-[1.05] tracking-tight text-slate-900">
              Scalable <span className="bg-gradient-to-r from-sky-500 via-cyan-500 to-blue-600 bg-clip-text text-transparent">cloud</span><br />
              performance for<br />
              your <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">web project.</span>
            </h1>
            <p className="mt-6 text-slate-600 text-lg leading-relaxed max-w-xl">
              DC10G powers 1000+ enterprises with Tier III certified infrastructure, carrier-neutral networks, and 24×7 expert operations — delivered at 99.99% uptime with zero-compromise security.
            </p>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
              {['Tier III Certified', 'DDoS Protected', 'ISO 27001', '99.99% SLA'].map((t) => (
                <div key={t} className="flex items-center gap-1.5 text-sm text-slate-700">
                  <CheckCircle2 size={14} className="text-sky-500" /> {t}
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#services" className="btn-primary">
                Explore Infrastructure <ArrowRight size={18} />
              </a>
              <a href="#about" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/70 backdrop-blur border border-sky-200 text-sky-700 font-semibold hover:bg-white hover:border-sky-400 transition-all">
                <Play size={14} /> Watch Overview
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              {heroStats.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.1 }}
                  className="p-3 rounded-xl bg-white/70 backdrop-blur border border-sky-100 hover:border-sky-300 transition-colors shadow-sm"
                >
                  <div className="text-2xl font-bold text-slate-900">{s.value}</div>
                  <div className="text-[10px] uppercase tracking-widest text-sky-600 mt-0.5">{s.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - 3D Cloud illustration with floating cards */}
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2 }} className="relative">
            <div className="relative aspect-square max-w-[560px] mx-auto">
              {/* Central cloud illustration */}
              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                className="relative w-full h-full rounded-full overflow-hidden"
                style={{ boxShadow: '0 30px 80px -20px rgba(14, 165, 233, 0.4)' }}
              >
                <img src={images.heroCloud} alt="Cloud Infrastructure" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-sky-500/20 via-transparent to-blue-500/10" />
              </motion.div>

              {/* Orbiting rings behind */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                  className="absolute w-[110%] h-[110%] rounded-full border border-dashed border-sky-300/50"
                />
              </div>

              {/* Floating metric card - top left */}
              <motion.div
                initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 }}
                className="absolute -left-4 top-8 w-44 p-4 rounded-2xl bg-white/95 backdrop-blur border border-sky-100 shadow-xl"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center"><Activity size={16} className="text-white" /></div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Uptime</div>
                </div>
                <div className="text-2xl font-black text-slate-900">99.99%</div>
                <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full w-[99%] bg-gradient-to-r from-emerald-400 to-sky-400" />
                </div>
              </motion.div>

              {/* Floating card - middle right */}
              <motion.div
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1 }}
                className="absolute -right-4 top-1/3 w-44 p-4 rounded-2xl bg-white/95 backdrop-blur border border-sky-100 shadow-xl"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center"><Zap size={16} className="text-white" /></div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Throughput</div>
                </div>
                <div className="text-2xl font-black text-slate-900">1.2M<span className="text-sm text-slate-500 ml-1">req/s</span></div>
                <div className="flex items-end gap-0.5 mt-2 h-5">
                  {[40, 60, 45, 70, 55, 80, 65, 90, 75, 85].map((h, i) => (
                    <div key={i} className="flex-1 bg-gradient-to-t from-purple-500 to-cyan-400 rounded-sm" style={{ height: `${h}%` }} />
                  ))}
                </div>
              </motion.div>

              {/* Bottom center pill */}
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }}
                className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/95 backdrop-blur border border-sky-100 shadow-xl"
              >
                <div className="flex items-center gap-1.5"><Shield size={14} className="text-emerald-500" /><span className="text-xs text-slate-800 font-semibold">DDoS Shield</span></div>
                <div className="w-px h-4 bg-slate-200" />
                <div className="flex items-center gap-1.5"><Cloud size={14} className="text-sky-500" /><span className="text-xs text-slate-800 font-semibold">Multi-Cloud</span></div>
              </motion.div>

              {/* Server icon floating - bottom left */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.3, type: 'spring' }}
                className="absolute bottom-8 -left-2 w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-xl cloud-float"
                style={{ animationDelay: '3s' }}
              >
                <Server className="text-white" size={22} />
              </motion.div>

              {/* Database icon floating - top right */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.4, type: 'spring' }}
                className="absolute top-8 -right-2 w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center shadow-xl cloud-float"
                style={{ animationDelay: '1s' }}
              >
                <Database className="text-white" size={22} />
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Bottom marquee */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
          className="mt-16 py-4 border-y border-sky-200/60 overflow-hidden relative bg-white/30 backdrop-blur rounded-2xl"
        >
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-sky-50 to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10" />
          <div className="flex marquee-track gap-12 text-sky-700/80 text-sm font-semibold whitespace-nowrap">
            {[...Array(2)].map((_, k) => (
              <React.Fragment key={k}>
                <span>Tier III Certified</span><span className="text-sky-300">•</span>
                <span>DDoS Protected</span><span className="text-sky-300">•</span>
                <span>Carrier Neutral</span><span className="text-sky-300">•</span>
                <span>99.99% Uptime SLA</span><span className="text-sky-300">•</span>
                <span>ISO 27001</span><span className="text-sky-300">•</span>
                <span>SOC 2 Type II</span><span className="text-sky-300">•</span>
                <span>PCI-DSS Compliant</span><span className="text-sky-300">•</span>
                <span>HIPAA Ready</span><span className="text-sky-300">•</span>
              </React.Fragment>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
